import React, { useCallback, useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

import Button from '@zenbusiness/zui/Button';

import { useGenerateArticle } from '#client/hooks/useGenerateArticle';

import * as Styled from './ArticleCard.style';
import {
  ARTICLE_IMAGES_TITLE_MAP,
  CLICKSTREAM_COMMON_PROPS,
  INDUSTRY_IMAGES
} from '../utils/constants';
import { highlightText } from '../utils/highlightText';
import VeloAvatar from '../VeloAvatar';

/**
 * A component for displaying article cards
 * @param {object} props - Component props
 * @param {string} props.businessIndustry - Article industry
 * @param {string} props.imageUrl - URL of the article image
 * @param {string} props.instructionId - Message UUID
 * @param {Function} props.onClick - Callback function when the article is clicked
 * @param {Function} props.onComponentClick - Callback function when the article is clicked
 * @param {string} props.summary - Article summary
 * @param {string} props.threadUuid - Thread UUID
 * @param {string} props.title - Article title
 * @returns {React.ReactElement} The ArticleCard component
 */
const ArticleCard = ({
  businessIndustry,
  imageUrl = '',
  instructionId,
  onClick = null,
  onComponentClick = null,
  summary,
  threadUuid,
  title
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Memoize the click data to avoid recreating on every render
  const clickData = useMemo(
    () => ({
      title,
      summary,
      imageUrl,
      businessIndustry
    }),
    [title, summary, imageUrl, businessIndustry]
  );

  const image =
    ARTICLE_IMAGES_TITLE_MAP[title] ||
    INDUSTRY_IMAGES[businessIndustry] ||
    imageUrl ||
    '/s2/raster/customer-industry-generic.png';

  const { generateArticle, loading, error } = useGenerateArticle();

  const handleCardClick = useCallback(
    async (event) => {
      // Prevent the click from bubbling up to the card root element
      if (event) {
        event.stopPropagation();
      }

      const result = await generateArticle(title, summary, threadUuid, instructionId);
      let fullMarkdown = result.data;

      // Handle error case - check response payload for error
      if (result && (!result.ok || result.error || error)) {
        const errorMessage =
          result.error?.message || 'An unexpected error occurred while generating the article.';
        fullMarkdown = `# Failed to Load Article

Sorry, we encountered an error while generating the article "${title}".

**Error Details:**
${errorMessage}

Please try again later or contact support if the problem persists.
`;
      } else {
        fullMarkdown = result.data;
      }

      if (onClick) {
        onClick(clickData);
      }

      if (onComponentClick) {
        onComponentClick({
          type: 'article',
          payload: {
            messageUuid: instructionId,
            title,
            markdown: fullMarkdown,
            imageUrl: image,
            summary
          }
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClick, onComponentClick, clickData, title, image, summary]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && onClick) {
        event.preventDefault();
        event.stopPropagation(); // Also prevent propagation for keyboard events
        handleCardClick(event);
      }
    },
    [onClick, handleCardClick]
  );

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(true); // Still hide placeholder even on error
  }, []);

  // Memoize alt text computation
  const altText = useMemo(() => `${businessIndustry} - 'Article image'`, [businessIndustry]);

  return (
    <Styled.Root aria-label={`Read article: ${title}`} role="article">
      <Styled.Content>
        {image && (
          <Styled.Media>
            {!imageLoaded && <Styled.ImagePlaceholder />}
            <Styled.Image
              alt={altText}
              loading="lazy"
              src={image}
              style={{
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
              }}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          </Styled.Media>
        )}

        <Styled.Header>
          <Styled.HeaderContainer aria-label="Toggle article summary" role="button" tabIndex={0}>
            <Styled.Title>{title}</Styled.Title>
          </Styled.HeaderContainer>
          <AnimatePresence initial={false}>
            {summary && (
              <Styled.SummaryContainer>
                <Styled.Description size="sm">{summary}</Styled.Description>
                <Button
                  clickstreamProps={{
                    ...CLICKSTREAM_COMMON_PROPS,
                    elementName: 'edu_article_cta'
                  }}
                  color="primary"
                  disabled={loading}
                  fullWidth
                  size="medium"
                  onClick={handleCardClick}
                  onKeyDown={handleKeyPress}
                >
                  {loading
                    ? highlightText('Retrieving Article', Styled.TextHighlight)
                    : highlightText('View Article', Styled.TextHighlight)}
                  &nbsp;
                  {loading ? (
                    <VeloAvatar color="transparent" height={30} state="answering" width={30} />
                  ) : (
                    <Styled.ArrowForwardIcon />
                  )}
                </Button>
              </Styled.SummaryContainer>
            )}
          </AnimatePresence>
        </Styled.Header>
      </Styled.Content>
    </Styled.Root>
  );
};

ArticleCard.propTypes = {
  businessIndustry: PropTypes.string,
  imageUrl: PropTypes.string,
  instructionId: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  summary: PropTypes.string.isRequired,
  threadUuid: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export default ArticleCard;
